import { compact, grouped } from "./grades";
import { sortGalleryItems } from "./marks";
import { parseAmount } from "./planner";
/** The limits in display order, each with its `plan.limits` label key (shared with the Gallery Planner). */
export const LEVEL_LIMITS = [
    { key: "coinBalance", label: "budget" },
    { key: "maxTaxLoss", label: "maxTaxLoss" },
    { key: "maxTransfers", label: "maxTransfers" },
    { key: "maxSets", label: "maxSets" },
];
// The season pass's levels each take 1,000 Season Points, so Max Level N is N * 1,000 SP.
export const SP_PER_SEASON_LEVEL = 1000;
// Every source starts excluded; the user includes one by hand.
export const DEFAULT_LEVEL_SETTINGS = {
    includeObjectivePlayers: false,
    includeSbcPlayers: false,
    maxSbcPlayerPrice: "",
    includeSeasonPlayers: false,
    maxSeasonLevel: "",
    includeSeasonPremium: false,
    maxPremiumSeasonLevel: "",
};
/** Customize is away from its defaults: its button says so. */
export const levelSettingsChanged = (settings) => Object.keys(DEFAULT_LEVEL_SETTINGS).some((key) => settings[key] !== DEFAULT_LEVEL_SETTINGS[key]);
/** Customize as a build sends it: max levels become Season Points. */
export const levelPlanOptions = (settings) => {
    const points = (level) => {
        const n = parseAmount(level);
        return n === undefined ? undefined : n * SP_PER_SEASON_LEVEL;
    };
    return {
        includeSbcPlayers: settings.includeSbcPlayers,
        maxSbcPlayerPrice: parseAmount(settings.maxSbcPlayerPrice),
        includeObjectivePlayers: settings.includeObjectivePlayers,
        includeSeasonPlayers: settings.includeSeasonPlayers,
        includeSeasonPremium: settings.includeSeasonPremium,
        maxSeasonPoints: points(settings.maxSeasonLevel),
        maxPremiumSeasonPoints: points(settings.maxPremiumSeasonLevel),
    };
};
/** Unlocked levels the user hasn't reached: the only ones a plan can aim for. */
export const pickableMilestones = (milestones) => milestones.filter((m) => m.threshold !== undefined && !m.reached);
/** The user's pick, else the stored plan's level while still pickable, else the next pickable one. */
export const targetLevel = (milestones, plan, picked) => {
    const pickable = pickableMilestones(milestones);
    return (picked ??
        (pickable.some((m) => m.level === plan?.level) ? plan?.level : undefined) ??
        pickable[0]?.level);
};
/** The highest level reached, 0 for none. */
export const currentGalleryLevel = (milestones) => milestones.reduce((top, m) => (m.reached ? Math.max(top, m.level) : top), 0);
/** A level's score threshold; undefined while it's locked or unknown. */
export const levelThreshold = (milestones, level) => milestones.find((m) => m.level === level)?.threshold;
/**
 * The limit fields as shown: what the user typed, else the stored plan's
 * limits, the coin balance the last Club Import's coins. Empty = no limit.
 */
export const levelLimitFields = (plan, coins, typed) => Object.fromEntries(LEVEL_LIMITS.map(({ key }) => {
    const stored = key === "coinBalance" ? (coins ?? plan?.coinBalance) : plan?.[key];
    return [
        key,
        typed[key] ?? (stored === undefined ? "" : grouped.format(stored)),
    ];
}));
/** The limit fields as a build sends them. */
export const levelPlanLimits = (fields) => Object.fromEntries(LEVEL_LIMITS.map(({ key }) => [key, parseAmount(fields[key])]));
/**
 * Updating first owners takes about 0.02 minutes per collected player: whole
 * minutes up to 5, then to the nearest 5. Undefined with nothing collected.
 */
export const firstOwnersMinutes = (collectedCount) => {
    const minutes = (collectedCount ?? 0) * 0.02;
    if (!minutes)
        return undefined;
    return minutes > 5
        ? Math.round(minutes / 5) * 5
        : Math.max(1, Math.round(minutes));
};
/** The build button's label, as a `gallery` key and its values. */
export const levelBuildLabel = ({ building, level, planLevel, }) => (building
    ? { key: planLevel === level ? "buttonUpdating" : "buttonBuilding" }
    : level === undefined
        ? { key: "build" }
        : {
            key: planLevel === level ? "updatePlan" : "buildPlan",
            values: { level },
        });
/**
 * A built plan's figures in display order; SBC price and objective players
 * only when the plan uses them.
 */
export const levelPlanStats = (plan, targetScore, coinBalance) => {
    const { totals } = plan;
    const of = (limit, format = compact.format) => limit === undefined ? undefined : format(limit);
    return [
        {
            key: "score",
            value: grouped.format(totals.score),
            of: of(targetScore),
            short: !plan.targetReached,
        },
        {
            key: "coinsNeeded",
            value: compact.format(totals.coinsNeeded),
            of: of(coinBalance),
            short: coinBalance !== undefined && totals.coinsNeeded > coinBalance,
        },
        {
            key: "taxLoss",
            value: compact.format(totals.taxLoss),
            of: of(plan.maxTaxLoss),
        },
        {
            key: "transfers",
            value: grouped.format(totals.transfers),
            of: of(plan.maxTransfers, grouped.format),
        },
        {
            key: "sets",
            value: grouped.format(totals.sets),
            of: of(plan.maxSets, grouped.format),
        },
        ...(totals.sbcPrice > 0
            ? [{ key: "sbcPrice", value: compact.format(totals.sbcPrice) }]
            : []),
        ...(totals.objectivePlayers > 0
            ? [
                {
                    key: "objectivePlayers",
                    value: grouped.format(totals.objectivePlayers),
                },
            ]
            : []),
    ];
};
// Regrade the sets you own first, then buy to improve the rest.
export const LEVEL_PLAN_GROUPS = ["improve", "buy"];
/** The plan's two steps, each in the plan's gallery order (category, then set priority), with how many are done. */
export const levelPlanGroups = (plan) => LEVEL_PLAN_GROUPS.map((group) => {
    const sets = plan.sets.filter((set) => set.group === group);
    return { group, sets, done: sets.filter((set) => set.done).length };
});
/** Runs of sets by category, in the order given: one list per category. */
export const levelPlanCategories = (sets) => sets.reduce((chunks, set) => {
    const last = chunks.at(-1);
    if (last?.category === set.category)
        last.sets.push(set);
    else
        chunks.push({ category: set.category, sets: [set] });
    return chunks;
}, []);
/** The step a plan opens on: the owned sets, until they're all done. */
export const firstLevelPlanGroup = (plan) => plan.sets.filter((s) => s.group === "improve").every((s) => s.done)
    ? "buy"
    : "improve";
/** The first set not done yet, across both steps: the one that starts open. */
export const firstUnfinishedLevelSet = (plan) => LEVEL_PLAN_GROUPS.flatMap((group) => plan.sets.filter((s) => s.group === group)).find((s) => !s.done);
/**
 * The open sets once `set` is marked done: it folds, and the next unfinished
 * set after it in its step opens, else the step's first unfinished one. The
 * last owned set done also opens the first set to buy.
 */
export const openSetsAfterDone = (plan, open, set) => {
    const sets = plan.sets.filter((s) => s.group === set.group);
    const index = sets.indexOf(set);
    const unfinished = sets.filter((s) => !s.done && s !== set);
    const next = unfinished.find((s) => sets.indexOf(s) > index) ?? unfinished[0];
    const firstBuy = set.group === "improve" && unfinished.length === 0
        ? plan.sets.find((s) => s.group === "buy" && !s.done)
        : undefined;
    return [
        ...open.filter((id) => id !== set.setId && id !== next?.setId),
        ...(next ? [next.setId] : []),
        ...(firstBuy && firstBuy.setId !== next?.setId ? [firstBuy.setId] : []),
    ];
};
/** The plan's progress: done sets' score, and cards ticked as got (capped at the plan's). */
export const levelPlanProgress = (plan, boughtCount) => ({
    doneScore: plan.sets
        .filter((set) => set.done)
        .reduce((sum, set) => sum + set.totalScore, 0),
    players: Math.min(boughtCount, plan.totals.players),
});
/**
 * A plan set's cards as the Gallery's lineup cards take them, by score: a
 * collected card is collected, an SBC player keeps its source. Each keeps its
 * `sbcSet` for a link to the SBC.
 */
export const levelPlanLineup = (items) => sortGalleryItems(items).map((item) => ({
    ...item,
    collected: item.source === "collected",
    source: item.source === "sbc" ? "sbc" : undefined,
}));
//# sourceMappingURL=levels.js.map